import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDataHeatMap } from '../../../hooks/useDataHeatMap';
import { Box } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { format } from 'date-fns';
import { AtCircularProgress } from '../../../components/AtCircularProgress';
import { useLocalStorage } from '../../../hooks/useLocalStorage';
import {
  CATEGORIAS,
  DIA_SEMANA,
  DIAS_SEMANA,
  FECHA,
  indicadores,
} from '../utils/variables';
import { ConfigTools, DEFAULT_CONFIG } from './ConfigTools';
import { HeatMap } from './HeatMap';

function calcularPorFecha(data, tipoIndicador) {
  const dataGrafico = [];
  if (!data) {
    return { fechas: [], dataGrafico: [], categorias: CATEGORIAS };
  }
  const fechas = data.map((availability) => {
    return availability.desde.substring(0, 10);
  });
  const fechasUnique = [...new Set(fechas)];
  fechasUnique.forEach((fecha, indexFecha) => {
    CATEGORIAS.forEach((hora, indexHora) => {
      let defaultValue = tipoIndicador.noData;
      const dataFecha = data.find(
        (item) => item.desde.substring(0, 13) === `${fecha} ${hora}`
      );
      if (dataFecha) {
        if (dataFecha.data[0]) {
          const {
            uptime,
            mantenimiento,
            parcial,
            downtime,
            no_monitoreo,
          } = dataFecha.data[0];
          if (tipoIndicador.name === 'uptime') {
            const sumaUptime = uptime + (mantenimiento ?? 0) + parcial;
            defaultValue = sumaUptime > 100 ? 100 : sumaUptime;
          } else {
            defaultValue = downtime;
          }
          if (no_monitoreo === 100) defaultValue = tipoIndicador.noData;
        }
      }
      dataGrafico.push([
        indexHora,
        indexFecha,
        parseFloat(defaultValue.toFixed(2)),
      ]);
    });
  });
  return {
    fechas: fechasUnique,
    dataGrafico,
    categorias: CATEGORIAS,
    height:
      120 +
      fechasUnique.length *
        (fechasUnique.length > 15 ? 30 : fechasUnique.length === 1 ? 65 : 45),
  };
}

function calcularPorDiaSemana(data, tipoIndicador) {
  const dataGrafico = [];
  if (!data) {
    return { fechas: [], dataGrafico: [], categorias: CATEGORIAS };
  }
  const fechas = data.map((availability) => {
    return availability.desde.substring(0, 10);
  });
  const dias = {
    Lunes: {},
    Martes: {},
    Miércoles: {},
    Jueves: {},
    Viernes: {},
    Sábado: {},
    Domingo: {},
  };
  const fechasUnique = [...new Set(fechas)];
  fechasUnique.forEach((fecha) => {
    CATEGORIAS.forEach((hora) => {
      let defaultValue = 0;
      const diaSemana =
        DIAS_SEMANA[format(new Date(`${fecha} 00:00:00`), 'i') - 1];
      const dataDia = dias[diaSemana];
      const dataFecha = data.find(
        (item) => item.desde.substring(0, 13) === `${fecha} ${hora}`
      );
      if (dataFecha) {
        if (dataFecha.data[0]) {
          const {
            uptime,
            mantenimiento,
            parcial,
            downtime,
          } = dataFecha.data[0];
          if (tipoIndicador.name === 'uptime') {
            const sumaUptime = uptime + (mantenimiento ?? 0) + parcial;
            defaultValue = sumaUptime > 100 ? 100 : sumaUptime;
          } else {
            defaultValue = downtime;
          }
        }
      }

      dias[diaSemana][hora] = {
        value: (dataDia[hora]?.value ?? 0) + defaultValue,
        total: (dataDia[hora]?.total ?? 0) + 1,
      };
    });
  });

  let countDias = 0;
  Object.entries(dias).forEach(([keyFecha, horas]) => {
    Object.entries(horas).forEach(([keyHora, hora]) => {
      dataGrafico.push([
        CATEGORIAS.indexOf(keyHora),
        DIAS_SEMANA.indexOf(keyFecha),
        parseFloat((hora.value / hora.total).toFixed(2)),
      ]);
    });
    if (Object.keys(horas).length) {
      countDias++;
    }
  });

  return {
    fechas: DIAS_SEMANA,
    dataGrafico,
    categorias: CATEGORIAS,
    height: 120 + countDias * (countDias > 15 ? 30 : countDias === 1 ? 65 : 45),
  };
}

export const HeatMapObjective = React.memo(function HeatMapObjective({
  objetivoId,
  globalStatePeriod,
  periodo,
  showDateHeatmap = true,
  showWeekdayHeatmap = true,
  refreshTime,
}) {
  const { t } = useTranslation();
  const { data, isError, isLoading, isFetching } = useDataHeatMap({
    objetivoId,
    globalStatePeriod,
    periodo,
    refreshTime,
  });
  const [heatmapSettings, setHeatmapSettings] = useLocalStorage(
    'heatmapSettings',
    DEFAULT_CONFIG
  );

  const dataHeatMapFecha = React.useMemo(() => {
    return calcularPorFecha(data, indicadores[heatmapSettings.tipoIndicador]);
  }, [data, heatmapSettings.tipoIndicador]);

  const dataHeatMapSemana = React.useMemo(() => {
    return calcularPorDiaSemana(
      data,
      indicadores[heatmapSettings.tipoIndicador]
    );
  }, [data, heatmapSettings.tipoIndicador]);

  if (isLoading || isFetching) {
    return <AtCircularProgress />;
  }

  if (isError) {
    return (
      <Alert severity="error">
        {t && typeof t === 'function'
          ? t('new-graph-availability-node.error') ||
            'Ocurrió un error al cargar el los datos de disponibilidad del objetivo. Vuelve a intentarlo o intenta con otro periodo.'
          : 'Ocurrió un error al cargar el los datos de disponibilidad del objetivo. Vuelve a intentarlo o intenta con otro periodo.'}
      </Alert>
    );
  }

  if (!isLoading && data && !data.length) {
    return (
      <Alert severity="info">
        {t && typeof t === 'function'
          ? t('new-graph-availability-node.no-data') ||
            'No se encontraron datos para el objetivo en el periodo indicado.'
          : 'No se encontraron datos para el objetivo en el periodo indicado.'}
      </Alert>
    );
  }

  return (
    <React.Fragment>
      <ConfigTools
        heatmapSettings={heatmapSettings}
        setHeatmapSettings={setHeatmapSettings}
      />
      <Box marginTop={2}>
        {showDateHeatmap && (
          <HeatMap
            tipoIndicador={heatmapSettings.tipoIndicador}
            dataHeatMap={dataHeatMapFecha}
            tipoGrafico={FECHA}
            graphTitle={'titleFecha'}
            oppositeAxis={heatmapSettings.oppositeAxis}
            colorIndicador={
              heatmapSettings.colorIndicador[heatmapSettings.tipoIndicador]
            }
          />
        )}
        {showWeekdayHeatmap && (
          <HeatMap
            tipoIndicador={heatmapSettings.tipoIndicador}
            dataHeatMap={dataHeatMapSemana}
            tipoGrafico={DIA_SEMANA}
            graphTitle={'titleSemana'}
            oppositeAxis={heatmapSettings.oppositeAxis}
            colorIndicador={
              heatmapSettings.colorIndicador[heatmapSettings.tipoIndicador]
            }
          />
        )}
      </Box>
    </React.Fragment>
  );
});
